import type { ReactElement } from 'react';
import { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AdminShell } from './components/AdminShell';
import { PublicShell } from './components/PublicShell';
import { AdminSessionProvider } from './lib/session';
import { PAGES } from './lib/registry';
import { AdminChallenge, AdminInvite, AdminRecovery, AdminSignIn } from './screens/AuthScreens';

/*
 * The operator console is the bulk of the bundle and no public visitor needs it,
 * so it is split out and fetched only when an /admin route is entered.
 */
const AdminRoute = lazy(() => import('./screens/AdminRoute').then((m) => ({ default: m.AdminRoute })));
const GamePreviewScreen = lazy(() => import('./screens/GamePreviewScreens').then((m) => ({ default: m.GamePreviewScreen })));

/**
 * Suspense boundary for one route element.
 *
 * It sits inside the shell on purpose: while the console chunk downloads, the
 * navigation stays on screen instead of the whole page blanking out.
 */
function LazyScreen({ children }: { children: ReactElement }) {
  return <Suspense fallback={<StateCard state="LOADING" />}>{children}</Suspense>;
}
import { PublicHome } from './screens/public/Home';
import { PublicGames } from './screens/public/Games';
import { PublicGameDetail } from './screens/public/GameDetail';
import { PublicNews, PublicNewsDetail } from './screens/public/News';
import { PublicEvents, PublicEventDetail } from './screens/public/Events';
import { SupportArticle, SupportHome, SupportNew, SupportTicket } from './screens/public/Support';
import { PublicIncident, PublicStatus } from './screens/public/Status';
import { PublicLegal } from './screens/public/Legal';
import { PublicPrivacyRequests } from './screens/public/Privacy';
import { PublicDestination } from './screens/public/Destination';
import { CompanyPage } from './screens/public/Company';
import {
  AccountRecovery,
  AccountSignIn,
  AccountSignUp,
  AccountVerify,
} from './screens/account/Auth';
import {
  AccountConnections,
  AccountData,
  AccountEntitlements,
  AccountHome,
  AccountInbox,
  AccountLink,
  AccountPreferences,
  AccountSecurity,
} from './screens/account/Pages';
import { StateCard } from './components/StateCard';
import './styles/tokens.css';
import './styles/app.css';
import './styles/public.css';

/**
 * Router for all 161 registered screens.
 *
 * Every route in registry/pages.json resolves to a real component; a direct
 * refresh or a back-navigation on any of them lands on the same screen.
 */

/** Public/account screens that have a bespoke implementation. */
const PUBLIC_COMPONENTS: Record<string, ReactElement> = {
  P001: <PublicHome />,
  P002: <PublicGames pageId="P002" />,
  P003: <PublicGameDetail />,
  P004: <PublicNews />,
  P005: <PublicNewsDetail />,
  P006: <PublicEvents />,
  P007: <PublicEventDetail />,
  P008: <SupportHome />,
  P009: <SupportArticle />,
  P010: <SupportNew />,
  P011: <SupportTicket />,
  P012: <PublicStatus />,
  P013: <PublicIncident />,
  P014: <PublicLegal />,
  P015: <PublicPrivacyRequests />,
  P016: <PublicDestination />,
  P017: <PublicGames pageId="P017" platformFilter={['ios', 'android']} />,
  P018: <PublicGames pageId="P018" platformFilter={['roblox']} />,
  P019: <PublicGames pageId="P019" platformFilter={['steam', 'pc']} />,
  P020: <PublicGames pageId="P020" lifecycleFilter="coming_soon" />,
  P021: <CompanyPage pageKey="about" />,
  P022: <CompanyPage pageKey="press" />,
  P023: <CompanyPage pageKey="careers" />,
  P024: <CompanyPage pageKey="contact" />,
};

const ACCOUNT_COMPONENTS: Record<string, ReactElement> = {
  U001: <AccountSignIn />,
  U002: <AccountSignUp />,
  U003: <AccountVerify />,
  U004: <AccountRecovery />,
  U005: <AccountHome />,
  U006: <AccountConnections />,
  U007: <AccountSecurity />,
  U008: <AccountPreferences />,
  U009: <AccountData />,
  U010: <AccountLink />,
  U011: <AccountInbox />,
  U012: <AccountEntitlements />,
};

/** Admin auth screens sit outside the console shell. */
const ADMIN_AUTH_COMPONENTS: Record<string, ReactElement> = {
  A001: <AdminSignIn />,
  A002: <AdminChallenge />,
  A003: <AdminRecovery />,
  A004: <AdminInvite />,
};

export default function App() {
  const publicPages = PAGES.pages.filter((page) => page.area === 'public');
  const accountPages = PAGES.pages.filter((page) => page.area === 'account');
  const adminPages = PAGES.pages.filter((page) => page.area === 'admin');
  const gamePages = PAGES.pages.filter((page) => page.area === 'game');

  return (
    <BrowserRouter>
      <AdminSessionProvider>
        <Routes>
          {/* public + optional account surfaces share the site shell */}
          <Route element={<PublicShell />}>
            {publicPages.map((page) => (
              <Route
                key={page.id}
                path={page.route}
                element={PUBLIC_COMPONENTS[page.id] ?? <StateCard state="NOT_FOUND" />}
              />
            ))}
            {accountPages.map((page) => (
              <Route
                key={page.id}
                path={page.route}
                element={ACCOUNT_COMPONENTS[page.id] ?? <StateCard state="NOT_FOUND" />}
              />
            ))}
          </Route>

          {/* operator auth, outside the console shell */}
          {Object.entries(ADMIN_AUTH_COMPONENTS).map(([pageId, element]) => {
            const page = adminPages.find((entry) => entry.id === pageId);
            return page ? <Route key={pageId} path={page.route} element={element} /> : null;
          })}

          {/* operator console */}
          <Route element={<AdminShell />}>
            {adminPages
              .filter((page) => !ADMIN_AUTH_COMPONENTS[page.id])
              .flatMap((page) => {
                const routes = [
                  <Route key={page.id} path={page.route} element={<LazyScreen><AdminRoute pageId={page.id} /></LazyScreen>} />,
                ];
                // /new and /:id/edit share one template, exactly as the design specifies.
                if (page.alias) {
                  routes.push(
                    <Route key={`${page.id}-alias`} path={page.alias} element={<LazyScreen><AdminRoute pageId={page.id} /></LazyScreen>} />,
                  );
                }
                return routes;
              })}
          </Route>

          {/* in-game UI preview (operator authenticated) */}
          {gamePages.map((page) => (
            <Route key={page.id} path={page.route} element={<LazyScreen><GamePreviewScreen pageId={page.id} /></LazyScreen>} />
          ))}

          {/* /admin is A005 itself (the operations dashboard), so it is not redirected. */}
          <Route element={<PublicShell />}>
            <Route path="*" element={<StateCard state="NOT_FOUND" />} />
          </Route>
        </Routes>
      </AdminSessionProvider>
    </BrowserRouter>
  );
}
